import { useTranslation } from 'react-i18next';
import type { CoverNoteData } from '../../types/coverNote';
import { isCivilAdviseCoverNote, isCivilAppellateCoverNote } from '../../types/coverNoteApi';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { coverNoteQrImageSrc } from '../../utils/coverNoteQr';
import { criminalCoverNoteDivisionUnit } from '../../utils/criminalCoverNoteDisplay';

function displayValue(value: string | null | undefined): string {
  const t = value?.trim();
  return t || '—';
}

export function CoverNoteBody({ coverNote }: { coverNote: CoverNoteData }) {
  const { t } = useTranslation();
  const qrSrc = coverNoteQrImageSrc(coverNote.qr);
  const civilAdviseView = isCivilAdviseCoverNote({ fileCategory: coverNote.fileCategory });
  const divisionUnitView =
    civilAdviseView || isCivilAppellateCoverNote({ fileCategory: coverNote.fileCategory });
  const divisionUnit = criminalCoverNoteDivisionUnit(
    coverNote.submittedDivisionName,
    coverNote.submittedAgency,
  );

  return (
    <>
      <div className="cn-meta cn-meta-qf">
        <div className="cn-qr-box">
          {qrSrc ? (
            <img
              src={qrSrc}
              alt={t('viewRequest.qrAlt')}
              width={68}
              height={68}
              style={{ display: 'block' }}
            />
          ) : (
            <div
              style={{
                width: 68,
                height: 68,
                background: 'var(--bg)',
                border: '1px dashed var(--border)',
                borderRadius: 4,
              }}
              aria-hidden
            />
          )}
          <div className="cn-qr-note">{t('caseRequests.qrNote')}</div>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.fileReferenceNumber')}</label>
          <p>{displayValue(coverNote.fileReferenceNumber)}</p>
        </div>
        <div className="cn-box">
          <label>{t('caseRequests.requestSubmittedAt')}</label>
          <p>{formatDisplayDateTime(coverNote.requestSubmittedAt) || displayValue(coverNote.requestSubmittedAt)}</p>
        </div>
        <div className="cn-box">
          <label>
            {divisionUnitView ? t('caseRequests.divisionUnit') : t('caseRequests.submittedAgency')}
          </label>
          <p>{displayValue(divisionUnitView ? divisionUnit : coverNote.submittedAgency)}</p>
        </div>
        <div className="cn-box">
          <label>{t('tasks.shared.fileCategory')}</label>
          <p className="cat-val">{displayValue(coverNote.fileCategory)}</p>
          {coverNote.subFileCategory?.trim() ? (
            <p className="sub">
              {t('caseRequests.subFileCategoryLabel', { value: coverNote.subFileCategory.trim() })}
            </p>
          ) : null}
        </div>
      </div>

      <div className="cn-sum-title">{t('caseRequests.summaryTitle')}</div>
      {civilAdviseView ? (
        <div className="cn-sum-grid">
          <div className="cn-col">
            <div className="csf">
              <label>{t('caseRequests.connectedRef')}</label>
              <p>{displayValue(coverNote.connectedAgdFileReferenceNumber)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.applicableAreas')}</label>
              <p>{displayValue(coverNote.applicableAreas)}</p>
            </div>
            <div className="csf">
              <label>{t('wizard.fields.description')}</label>
              <p>{displayValue(coverNote.description)}</p>
            </div>
          </div>
          <div className="cn-col">
            <div className="csf">
              <label>{t('caseRequests.urgencyLevel')}</label>
              <p>{displayValue(coverNote.urgencyLevel)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.deadline')}</label>
              <p>{formatDisplayDate(coverNote.deadline) || displayValue(coverNote.deadline)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.urgencyJustification')}</label>
              <p>{displayValue(coverNote.urgencyJustification)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.documentsDeclared')}</label>
              <div className="dtags">
                {(coverNote.documents?.length ? coverNote.documents : ['None']).map((doc, index) => (
                  <div key={`${doc}-${index}`} className="dtag">
                    {doc !== 'None' ? (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : null}
                    {doc}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="cn-sum-grid">
          <div className="cn-col">
            <div className="csf">
              <label>{t('caseRequests.caseSource')}</label>
              <p>{displayValue(coverNote.caseSource)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.connectedRef')}</label>
              <p>{displayValue(coverNote.connectedAgdFileReferenceNumber)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.accusedName')}</label>
              <p>{displayValue(coverNote.accusedOrSuspectName)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.applicableAreas')}</label>
              <p>{displayValue(coverNote.applicableAreas)}</p>
            </div>
            <div className="csf">
              <label>{t('wizard.fields.description')}</label>
              <p>{displayValue(coverNote.description)}</p>
            </div>
          </div>
          <div className="cn-col">
            <div className="csf">
              <label>{t('caseRequests.urgencyLevel')}</label>
              <p>{displayValue(coverNote.urgencyLevel)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.deadline')}</label>
              <p>{formatDisplayDate(coverNote.deadline) || displayValue(coverNote.deadline)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.urgencyJustification')}</label>
              <p>{displayValue(coverNote.urgencyJustification)}</p>
            </div>
            <div className="csf">
              <label>{t('caseRequests.documentsDeclared')}</label>
              <div className="dtags">
                {(coverNote.documents?.length ? coverNote.documents : ['None']).map((doc, index) => (
                  <div key={`${doc}-${index}`} className="dtag">
                    {doc !== 'None' ? (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : null}
                    {doc}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
